import { supabase } from '@/integrations/supabase/client';
import { getFreePlanLimits } from './freePlanLimitsService';

export type OpositorModule = 'tests' | 'cases' | 'flashcards' | 'materials';

export interface OpositorUsage {
  tests: number;
  cases: number;
  flashcards: number;
  materials: number;
}

/** Valores por defecto si no se han configurado en admin */
export const FREE_OPOSITOR_LIMITS: OpositorUsage = {
  tests: 10,
  cases: 2,
  flashcards: 20,
  materials: 2,
};

/** Lee los límites configurados desde admin (con cache) */
export async function getOpositorLimits(): Promise<OpositorUsage> {
  const limits = await getFreePlanLimits();
  return {
    tests: limits.opositor_tests,
    cases: limits.opositor_cases,
    flashcards: limits.opositor_flashcards,
    materials: limits.opositor_materials,
  };
}

export const MODULE_LABELS: Record<OpositorModule, string> = {
  tests: 'tests',
  cases: 'casos prácticos',
  flashcards: 'flashcards',
  materials: 'materiales (presentación/resumen/infografía)',
};

const ZERO_USAGE: OpositorUsage = { tests: 0, cases: 0, flashcards: 0, materials: 0 };

interface CacheEntry {
  userId: string;
  usage: OpositorUsage;
  timestamp: number;
}

const CACHE_TTL = 30_000; // 30 s — refrescamos rápido para que el contador diario reaccione tras cada generación
let cache: CacheEntry | null = null;

export function invalidateOpositorQuotaCache() {
  cache = null;
}

/**
 * Devuelve el uso del MES en curso (tests, casos, flashcards y materiales)
 * para el usuario actual, contra los datos reales de las tablas opositor_*.
 * El contador se reinicia el primer día de cada mes (hora del servidor).
 */
export async function getOpositorUsage(userId: string): Promise<OpositorUsage> {
  if (!userId) return { ...ZERO_USAGE };

  if (cache && cache.userId === userId && Date.now() - cache.timestamp < CACHE_TTL) {
    return cache.usage;
  }

  try {
    const { data, error } = await (supabase.rpc as any)('get_opositor_free_usage', {
      p_user_id: userId,
    });

    if (error) {
      console.warn('[opositorQuota] RPC error:', error);
      return { ...ZERO_USAGE };
    }

    const usage: OpositorUsage = {
      tests: Number((data as any)?.tests ?? 0),
      cases: Number((data as any)?.cases ?? 0),
      flashcards: Number((data as any)?.flashcards ?? 0),
      materials: Number((data as any)?.materials ?? 0),
    };

    cache = { userId, usage, timestamp: Date.now() };
    return usage;
  } catch (err) {
    console.warn('[opositorQuota] unexpected error:', err);
    return { ...ZERO_USAGE };
  }
}

/**
 * `true` si el plan/perfil debe estar sujeto a las cuotas mensuales.
 * Solo aplica al plan 'free' (los planes de oposiciones de pago son ilimitados).
 * Admins y betatesters quedan exentos.
 */
export function isFreeOpositor(plan: string | undefined | null, profile: any): boolean {
  if (!plan || plan !== 'free') return false;
  if (profile?.is_betatester) return false;
  if (profile?.role === 'admin' || profile?.role === 'super_admin') return false;
  return true;
}

export interface QuotaCheckResult {
  allowed: boolean;
  used: number;
  limit: number;
  remaining: number;
}

/**
 * Comprueba si el usuario puede generar un nuevo recurso del módulo indicado.
 * Para planes que no sean 'free' devuelve siempre allowed=true (ilimitado).
 */
export async function checkOpositorQuota(
  userId: string,
  plan: string | undefined | null,
  profile: any,
  module: OpositorModule
): Promise<QuotaCheckResult> {
  const limits = await getOpositorLimits();
  const limit = limits[module];

  if (!isFreeOpositor(plan, profile)) {
    return { allowed: true, used: 0, limit: Infinity, remaining: Infinity };
  }

  const usage = await getOpositorUsage(userId);
  const used = usage[module] ?? 0;
  const remaining = Math.max(0, limit - used);
  return {
    allowed: used < limit,
    used,
    limit,
    remaining,
  };
}
